import React, { useState } from 'react';
import { useGetPage } from 'hooks/useGetPage';
import { PreWorkSectionProps } from '../interfaces';
import { pageIds } from 'utilities/constants';
import Loader from 'components/loaderComponent';
import { useGetFrontAndBackDots } from 'app/my-skin/components/BodySymtomsLocation/hooks';
import { GetAppointmentMediaOutput } from 'graphql/generated/remote-schema-hasura';
import { useGetSignUrlFromStorageForRequesterLazyQuery } from 'graphql/generated/hasura';
import ChatMediaModal from '../../../app/my-appointments/components/ChatMediaModal';
import ImageMiniComponent from '../../ImageMiniComponent';
import { twMerge } from 'tailwind-merge';

const PreWorkSection: React.FC<PreWorkSectionProps> = ({
  locale,
  appointmentData,
}) => {
  const [src, setSrc] = useState<string | null | undefined>(null);
  const { data: preworkLocales, loading: preworkLocalesLoading } = useGetPage({
    locale: 'en',
    pageId: pageIds.APPOINTMENT_PREWORK,
  });

  const { frontDots: allFrontDots } = useGetFrontAndBackDots();

  const frontLocations = new Set(allFrontDots?.map((dot) => dot.location));

  if (preworkLocalesLoading) return <Loader />;

  return (
    <div className="w-full flex flex-col gap-5">
      <div className="text-btn-black">
        <div>
          <div className="font-semibold text-left">
            {locale.reasonOfAppointment}
          </div>
          <div className="text-left ">
            <span
              className="text-med-gray-3"
              data-testid={`appointment-subject-${appointmentData?.subjectOfAppointment}`}
            >
              {appointmentData?.subjectOfAppointment}
            </span>
          </div>
        </div>
      </div>

      <div
        className={twMerge(
          'rounded',
          !appointmentData?.appointmentNotes?.symptomsTypes?.[0] && 'hidden',
        )}
      >
        <div className="font-semibold text-left">
          {preworkLocales.symptomTypes}
        </div>
        <ul
          className="text-left list-disc pl-5"
          data-testid="appointment-symptom-list"
        >
          {appointmentData?.appointmentNotes?.symptomsTypes?.map((symptom) => (
            <li className="text-med-gray-3" key={symptom}>
              {symptom}
            </li>
          ))}
        </ul>
      </div>

      <div
        className={twMerge(
          'rounded',
          !appointmentData?.appointmentNotes?.howLongQuestion && 'hidden',
        )}
      >
        <div className="font-semibold text-left">
          {preworkLocales.howLongTitle}
        </div>
        <div className="text-left list-disc">
          <div
            className="text-med-gray-3 font-medium"
            data-testid="symptoms-how-long"
          >
            {appointmentData?.appointmentNotes?.howLongQuestion || '-'}
          </div>
        </div>
      </div>

      <div
        className={twMerge(
          'rounded',
          !appointmentData?.appointmentNotes?.severityQuestion && 'hidden',
        )}
      >
        <div className="font-semibold text-left">
          {preworkLocales.severityQuestion}
        </div>
        <div className="text-left list-disc">
          <div
            className="text-med-gray-3 font-medium"
            data-testid="symptoms-severity"
          >
            {appointmentData?.appointmentNotes?.severityQuestion || '-'}
          </div>
        </div>
      </div>

      <div
        className={twMerge(
          'rounded',
          !appointmentData?.appointmentNotes?.anythingElseQuestion && 'hidden',
        )}
      >
        <div className="font-semibold text-left">
          {preworkLocales.additionalNotes}
        </div>
        <ul className="text-left">
          <li
            className="text-med-gray-3 whitespace-pre-wrap"
            data-testid="appointment-additional-info"
          >
            {appointmentData?.appointmentNotes?.anythingElseQuestion || '-'}
          </li>
        </ul>
      </div>

      {!!appointmentData?.media?.length && (
        <>
          <div className="text-h4 font-semibold capitalize mt-2.5 text-left">
            {preworkLocales.symptomsLabel}
          </div>
          <div className="rounded">
            <div className="text-left" data-testid="back-symptoms-location">
              {appointmentData.media.map((symptom, index, array) => {
                if (!symptom?.bodySite) return null;

                return (
                  <React.Fragment
                    key={(symptom as GetAppointmentMediaOutput).fileId}
                  >
                    <SymptomsItem
                      symptom={symptom as GetAppointmentMediaOutput}
                      sideTitle={
                        frontLocations.has(symptom.bodySite)
                          ? preworkLocales.front
                          : preworkLocales.back
                      }
                      onClick={(url?: string) => setSrc(url)}
                      codexPatientId={appointmentData.codexPatientId}
                    />

                    {array.length - 1 > index && (
                      <hr className="w-full my-2.5" />
                    )}
                  </React.Fragment>
                );
              })}
            </div>

            <ChatMediaModal src={src} setSrc={setSrc} />
          </div>
        </>
      )}
    </div>
  );
};

export default PreWorkSection;

export const SymptomsItem = ({
  symptom,
  sideTitle,
  codexPatientId,
  onClick,
}: {
  symptom?: GetAppointmentMediaOutput;
  onClick: (url?: string) => void;
  sideTitle: string;
  codexPatientId: string;
}) => {
  const [getUrl] = useGetSignUrlFromStorageForRequesterLazyQuery();

  const reschedule = async (mediaId?: string) => {
    if (!mediaId) {
      return;
    }
    const url = await getUrl({
      variables: {
        fileId: symptom?.fileId ?? '',
        requestedUserId: codexPatientId,
      },
    });
    return url?.data?.GetSignUrlFromStorageForRequester?.url;
  };

  return (
    <div key={symptom?.fileId} className="text-btn-black">
      <div className="flex flex-col desktop:flex-row gap-2.5">
        <div className={twMerge(!symptom?.fileId && 'hidden')}>
          <ImageMiniComponent
            className="w-[150px] h-[150px] object-cover cursor-pointer rounded-5"
            setSrc={onClick}
            mediaId={symptom?.fileId as string}
            altText={`${symptom?.bodySite} image`}
            initialMediaUrl={undefined}
            maxRetry={2}
            reschedule={reschedule}
          />
        </div>
        <div className="flex flex-col">
          <div className="text-title font-semibold capitalize mb-1">
            {sideTitle}
          </div>
          <div className="font-semibold">{symptom?.bodySite}</div>

          <div className="text-med-gray-3">{symptom?.description}</div>
        </div>
      </div>
    </div>
  );
};
